import React, { useState, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Modal from './Modal';
import './CampaignTemplateEditor.css';

// Custom Table Blot - same as TemplatesPage
const BlockEmbed = Quill.import('blots/block/embed');

// Helper to clean table HTML (remove Google Sheets junk)
const cleanTableHTML = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const table = temp.querySelector('table');

  if (!table) return html;

  // Remove unnecessary attributes
  const attributesToRemove = ['xmlns', 'data-sheets-root', 'data-sheets-baot', 'cellspacing', 'cellpadding', 'dir'];
  attributesToRemove.forEach(attr => table.removeAttribute(attr));

  // Clean all child elements (remove inline styles, keep structure)
  const cleanElement = (element) => {
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-sheets-') || attr.name === 'style' || attr.name === 'class') {
        element.removeAttribute(attr.name);
      }
    });
    Array.from(element.children).forEach(child => cleanElement(child));
  };

  // Remove colgroup
  const colgroup = table.querySelector('colgroup');
  if (colgroup) colgroup.remove();

  // Clean all child elements
  cleanElement(table);

  // Set simple border attribute
  table.setAttribute('border', '1');

  return table.outerHTML;
};

class TableBlot extends BlockEmbed {
  static blotName = 'table';
  static tagName = 'table';
  static className = 'ql-table';

  static create(value) {
    const node = super.create();
    if (typeof value === 'string') {
      const cleanedHTML = cleanTableHTML(value);
      const temp = document.createElement('div');
      temp.innerHTML = cleanedHTML;
      const table = temp.querySelector('table');

      if (table) {
        Array.from(table.attributes).forEach(attr => {
          node.setAttribute(attr.name, attr.value);
        });
        node.innerHTML = table.innerHTML;
      } else {
        node.innerHTML = cleanedHTML;
      }
    }
    return node;
  }

  static value(node) {
    return node.outerHTML;
  }
}

Quill.register(TableBlot, true);

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false,
    matchers: [
      ['TABLE', (node, delta) => {
        const Delta = Quill.import('delta');
        const tableHTML = node.outerHTML;
        return new Delta().insert({ table: tableHTML });
      }],
      ['TBODY', () => new (Quill.import('delta'))()],
      ['THEAD', () => new (Quill.import('delta'))()],
      ['TR', () => new (Quill.import('delta'))()],
      ['TD', () => new (Quill.import('delta'))()],
      ['TH', () => new (Quill.import('delta'))()]
    ]
  }
};

const CampaignTemplateEditor = ({ isOpen, onClose, campaign, template, onSave, previewMode = false }) => {
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [customMessages, setCustomMessages] = useState([]);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [htmlEditorStates, setHtmlEditorStates] = useState({});
  const [saving, setSaving] = useState(false);

  const isMultiMessage = Array.isArray(template?.json_config?.messages) &&
                         template.json_config.messages.length > 1;

  useEffect(() => {
    if (isOpen && template) {
      // Load existing overrides or template defaults
      const overrides = campaign?.template_overrides;

      if (isMultiMessage) {
        // Multi-message template
        if (overrides?.messages) {
          // Load from overrides
          setCustomMessages(overrides.messages.map(msg => ({
            subject: msg.subject || '',
            body: msg.body || '',
            type: msg.type || 'email',
            provider: msg.provider || 'smtp',
            delay_hours: msg.delay_hours || 0
          })));
        } else {
          // Load from template
          setCustomMessages(template.json_config.messages.map(msg => ({
            subject: msg.subject || '',
            body: msg.body || '',
            type: msg.type || 'email',
            provider: msg.provider || 'smtp',
            delay_hours: msg.delay_hours || 0
          })));
        }
      } else {
        // Single-message template
        if (overrides?.subject || overrides?.body) {
          // Load from overrides
          setCustomSubject(overrides.subject || '');
          setCustomBody(overrides.body || '');
        } else {
          // Load from template
          setCustomSubject(template.subject || '');
          setCustomBody(template.content || template.body || '');
        }
      }
    }
  }, [isOpen, campaign, template, isMultiMessage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const templateOverrides = isMultiMessage
        ? { messages: customMessages }
        : { subject: customSubject, body: customBody };

      if (previewMode) {
        // Preview mode: just return data via callback, don't save to backend
        onSave(templateOverrides);
        onClose();
      } else {
        // Normal mode: save to backend
        await onSave(templateOverrides);
        // Close modal on success
        onClose();
      }
    } catch (error) {
      console.error('Error saving template changes:', error);
      // Close modal even on error so user isn't stuck
      onClose();
      // Error message will be shown by parent component (CampaignsPage)
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Campaign Template" size="large">
      <div className="campaign-template-editor">
        <div className="editor-notice">
          <strong>Note:</strong> {previewMode
            ? 'Customise the template for your new campaign. Changes will be applied when you create the campaign.'
            : 'Changes here only affect this campaign, not the original template.'}
        </div>

        {isMultiMessage ? (
          <div className="multi-message-editor">
            <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>
              Edit Messages ({customMessages.length} messages)
            </h4>
            {customMessages.map((msg, index) => (
              <div
                key={index}
                className="message-editor-section"
                style={{
                  marginBottom: index < customMessages.length - 1 ? '24px' : '0',
                  paddingBottom: index < customMessages.length - 1 ? '24px' : '0',
                  borderBottom: index < customMessages.length - 1 ? '1px solid var(--border-color)' : 'none'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)', fontSize: '15px' }}>
                  Message {index + 1}
                </div>

                {msg.type === 'email' && (
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Subject</label>
                    <input
                      type="text"
                      value={msg.subject}
                      onChange={(e) => {
                        const updated = [...customMessages];
                        updated[index].subject = e.target.value;
                        setCustomMessages(updated);
                      }}
                      placeholder="Email subject line"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Body</label>
                  {msg.type === 'email' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {htmlEditorStates[index] ? 'HTML Editor' : 'Visual Editor'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setHtmlEditorStates(prev => ({ ...prev, [index]: !prev[index] }))}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          border: '1px solid var(--border-strong)',
                          borderRadius: '4px',
                          background: 'white',
                          cursor: 'pointer',
                          color: 'var(--text-body)'
                        }}
                      >
                        {htmlEditorStates[index] ? '← Visual Editor' : 'HTML Editor →'}
                      </button>
                    </div>
                  )}
                  {msg.type === 'email' ? (
                    htmlEditorStates[index] ? (
                      <textarea
                        value={msg.body}
                        onChange={(e) => {
                          const updated = [...customMessages];
                          updated[index].body = e.target.value;
                          setCustomMessages(updated);
                        }}
                        placeholder="Paste HTML here..."
                        rows={10}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid var(--border-strong)',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          resize: 'vertical'
                        }}
                      />
                    ) : (
                      <ReactQuill
                        value={msg.body}
                        onChange={(content) => {
                          const updated = [...customMessages];
                          updated[index].body = content;
                          setCustomMessages(updated);
                        }}
                        modules={quillModules}
                        placeholder="Message content..."
                        theme="snow"
                      />
                    )
                  ) : (
                    <textarea
                      value={msg.body}
                      onChange={(e) => {
                        const updated = [...customMessages];
                        updated[index].body = e.target.value;
                        setCustomMessages(updated);
                      }}
                      placeholder="SMS message content..."
                      rows={6}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="single-message-editor">
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>Subject</label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Email subject line"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="form-group">
              <label>Body</label>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {showHtmlEditor ? 'HTML Editor' : 'Visual Editor'}
                </span>
                <button
                  type="button"
                  onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: 'pointer',
                    color: 'var(--text-body)'
                  }}
                >
                  {showHtmlEditor ? '← Visual Editor' : 'HTML Editor →'}
                </button>
              </div>
              {showHtmlEditor ? (
                <textarea
                  value={customBody}
                  onChange={(e) => setCustomBody(e.target.value)}
                  placeholder="Paste HTML here..."
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    resize: 'vertical'
                  }}
                />
              ) : (
                <ReactQuill
                  value={customBody}
                  onChange={(content) => setCustomBody(content)}
                  modules={quillModules}
                  placeholder="Message content..."
                  theme="snow"
                />
              )}
            </div>
          </div>
        )}

        <div className="editor-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CampaignTemplateEditor;
